import React, { useState } from 'react';
import apiClient from '../config/axiosConfig';

interface AddLocationProps {
  onLocationAdded: () => void;
  onCancel: () => void;
}

interface LocationFormData {
  name: string;
  address_line1: string;
  postal_code: string;
  city: string;
  max_recurrence_weeks: string;
}

const MIN_RECURRENCE_WEEKS = 1;
const MAX_RECURRENCE_WEEKS = 52;

const AddLocation: React.FC<AddLocationProps> = ({ onLocationAdded, onCancel }) => {
  const [formData, setFormData] = useState<LocationFormData>({
    name: '',
    address_line1: '',
    postal_code: '',
    city: '',
    max_recurrence_weeks: '12'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Locatienaam is verplicht';
    }
    if (!formData.address_line1.trim()) {
      newErrors.address_line1 = 'Adres is verplicht';
    }
    if (!formData.postal_code.trim()) {
      newErrors.postal_code = 'Postcode is verplicht';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'Stad is verplicht';
    }

    const weeks = parseInt(formData.max_recurrence_weeks, 10);
    if (!Number.isInteger(weeks) || weeks < MIN_RECURRENCE_WEEKS || weeks > MAX_RECURRENCE_WEEKS) {
      newErrors.max_recurrence_weeks = `Vul een aantal weken in tussen ${MIN_RECURRENCE_WEEKS} en ${MAX_RECURRENCE_WEEKS}`;
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await apiClient.post('/locations', {
        ...formData,
        max_recurrence_weeks: parseInt(formData.max_recurrence_weeks, 10)
      });
      onLocationAdded();
    } catch (error: any) {
      console.error('Error creating location:', error);
      if (error.response?.data?.details) {
        setError(error.response.data.details.join(', '));
      } else {
        setError('Er is een fout opgetreden bij het toevoegen van de locatie.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-content">
      <div className="add-location-form">
        <div className="form-header">
          <h3>Nieuwe Locatie Toevoegen</h3>
          <button 
            className="close-btn"
            onClick={onCancel}
            type="button"
          >
            ×
          </button>
        </div>

      <form onSubmit={handleSubmit} className="location-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="name" className="required">
            Locatienaam
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Bijv. Centraal Station"
            className={`form-input${fieldErrors.name ? ' error' : ''}`}
          />
          {fieldErrors.name && <span className="field-error">{fieldErrors.name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="address_line1" className="required">
            Adres
          </label>
          <input
            type="text"
            id="address_line1"
            name="address_line1"
            value={formData.address_line1}
            onChange={handleInputChange}
            placeholder="Bijv. Stationsplein 1"
            className={`form-input${fieldErrors.address_line1 ? ' error' : ''}`}
          />
          {fieldErrors.address_line1 && <span className="field-error">{fieldErrors.address_line1}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="postal_code" className="required">
              Postcode
            </label>
            <input
              type="text"
              id="postal_code"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleInputChange}
              placeholder="1234 AB"
              className={`form-input${fieldErrors.postal_code ? ' error' : ''}`}
            />
            {fieldErrors.postal_code && <span className="field-error">{fieldErrors.postal_code}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="city" className="required">
              Stad
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              placeholder="Amsterdam"
              className={`form-input${fieldErrors.city ? ' error' : ''}`}
            />
            {fieldErrors.city && <span className="field-error">{fieldErrors.city}</span>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="max_recurrence_weeks">
            Herhalende reservering: aantal weken vooruit
          </label>
          <input
            type="number"
            id="max_recurrence_weeks"
            name="max_recurrence_weeks"
            min={MIN_RECURRENCE_WEEKS}
            max={MAX_RECURRENCE_WEEKS}
            step={1}
            value={formData.max_recurrence_weeks}
            onChange={handleInputChange}
            className={`form-input${fieldErrors.max_recurrence_weeks ? ' error' : ''}`}
          />
          {fieldErrors.max_recurrence_weeks && (
            <span className="field-error">{fieldErrors.max_recurrence_weeks}</span>
          )}
          <span className="field-hint">
            Hoe ver vooruit iemand een fiets van deze locatie wekelijks mag laten terugkomen.
            Losse reserveringen vallen hier buiten; die mogen tot een jaar vooruit.
          </span>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={isSubmitting}
          >
            Annuleren
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Toevoegen...' : 'Locatie Toevoegen'}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default AddLocation;
